<template>
  <div>
    <b-form-group label="Title">
      <b-input v-model="content.title" placeholder="e.g Awesome Media" />
    </b-form-group>

    <b-form-group label="Text (optional)">
      <b-input v-model="content.text" placeholder="e.g lorem ipsum" />
    </b-form-group>

    <div class="mb-2">Bullet Points</div>

    <template v-if="content.bullet_points">
      <b-row v-for="(point, index) in content.bullet_points" :key="index" class="mb-3">
        <b-col>
          <b-input-group>
            <b-form-input
              placeholder="e.g Wonderful tracks"
              v-model="content.bullet_points[index].text"
              style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
            />

            <template #append>
              <b-button
                variant="danger"
                @click="() => removeBulletPoint(index)"
              >
                <strong>&times;</strong>
              </b-button>
            </template>
          </b-input-group>
        </b-col>
      </b-row>
    </template>

    <b-button variant="light" size="sm" @click="addEmptyBulletPoint">
      Add Point
    </b-button>

    <div class="mb-2 mt-3">Media position</div>

    <div class="d-flex">
      <span @click="content.position_right = false" class="mr-2 cursor-pointer">
        Left
      </span>

      <b-form-checkbox v-model="content.position_right" name="position_right" switch></b-form-checkbox>

      <span @click="content.position_right = true" class="cursor-pointer">
        Right
      </span>
    </div>

    <div class="mb-2 mt-3">Media</div>

    <input
      class="invisible no-dimensions"
      accept="video/mp4,video/x-m4v,video/*,image/*"
      ref="desktopMedia"
      type="file"
      @change="uploadDesktopMedia"
    />
    <b-button variant="primary" class="mb-2" @click="pickDesktopMedia" :disabled="uploading.desktop">
      <b-spinner v-show="uploading.desktop" class="mr-2" small label="Loading" />
      {{ content.media.desktop ? 'Upload different media' : 'Upload media' }}
    </b-button>

    <div class="w-25 h-auto my-2">
      <b-embed
        type="video"
        :src="content.media.desktop"
        allowfullscreen
        controls
        v-if="content.media.desktop"
      />
    </div>

    <b-form-checkbox v-model="content.media.tablet_same_as_desktop" @change="toggleSameMediaForTablet">
      Use the same media for tablets
    </b-form-checkbox>

    <div class="mt-2" v-if="!content.media.tablet_same_as_desktop">
      <input
        class="invisible no-dimensions"
        accept="video/mp4,video/x-m4v,video/*,image/*"
        ref="tabletMedia"
        type="file"
        @change="uploadTabletMedia"
      />
      <b-button variant="primary" class="mb-2" @click="pickTabletMedia" :disabled="uploading.tablet">
        <b-spinner v-show="uploading.tablet" class="mr-2" small label="Loading" />
        {{ content.media.tablet ? 'Upload different media' : 'Upload media' }}
      </b-button>

      <div class="w-25 h-auto my-2">
        <b-embed
          type="video"
          :src="content.media.tablet"
          allowfullscreen
          controls
          v-if="content.media.tablet"
        />
      </div>
    </div>

    <b-form-checkbox v-model="content.media.mobile_same_as_desktop" @change="toggleSameMediaForMobile">
      Use the same media for mobile
    </b-form-checkbox>

    <div class="mt-2" v-if="!content.media.mobile_same_as_desktop">
      <input
        class="invisible no-dimensions"
        accept="video/mp4,video/x-m4v,video/*,image/*"
        ref="mobileMedia"
        type="file"
        @change="uploadMobileMedia"
      />
      <b-button variant="primary" class="mb-2" @click="pickMobileMedia" :disabled="uploading.mobile">
        <b-spinner v-show="uploading.mobile" class="mr-2" small label="Loading" />
        {{ content.media.mobile ? 'Upload different media' : 'Upload media' }}
      </b-button>

      <div class="w-25 h-auto my-2">
        <b-embed
          type="video"
          :src="content.media.mobile"
          allowfullscreen
          controls
          v-if="content.media.mobile"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { media as mediaApi } from '@/api'

export default {
  name: 'PagesMediaWidget',

  props: {
    value: {
      type: Object
    },
  },

  data() {
    return {
      content: this.value,
      resolved: {
        type: Object
      },
      uploading: {
        desktop: false,
        tablet: false,
        mobile: false,
      },
    }
  },

  methods: {
    addEmptyBulletPoint() {
      this.content.bullet_points.push({ text: '' })
    },

    removeBulletPoint(index) {
      if (this.content.bullet_points.length === 1) {
        this.content.bullet_points = []
        return
      }

      this.content.bullet_points.splice(index, 1)
    },

    toggleSameMediaForTablet(value) {
      this.content.media.tablet_same_as_desktop = value

      if (!value) {
        this.content.media.tablet = null
      }
    },

    toggleSameMediaForMobile(value) {
      this.content.media.mobile_same_as_desktop = value

      if (!value) {
        this.content.media.mobile = null
      }
    },

    pickDesktopMedia() {
      this.$refs.desktopMedia.click()
    },

    pickTabletMedia() {
      this.$refs.tabletMedia.click()
    },

    pickMobileMedia() {
      this.$refs.mobileMedia.click()
    },

    async uploadDesktopMedia(event) {
      try {
        this.uploading.desktop = true
        await this.$store.dispatch('auth/refreshToken');
        const media = await mediaApi.media(event.target.files[0], 'media-block')
        this.content.media.desktop = media.data.url
        this.uploading.desktop = false
      } catch (error) {
        this.uploading.desktop = false
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong.',
          variant: 'danger',
        })
      }
    },

    async uploadTabletMedia(event) {
      try {
        this.uploading.tablet = true
        await this.$store.dispatch('auth/refreshToken');
        const media = await mediaApi.media(event.target.files[0], 'media-block')
        this.content.media.tablet = media.data.url
        this.uploading.tablet = false
      } catch (error) {
        this.uploading.tablet = false
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong.',
          variant: 'danger',
        })
      }
    },

    async uploadMobileMedia(event) {
      try {
        this.uploading.mobile = true
        await this.$store.dispatch('auth/refreshToken');
        const media = await mediaApi.media(event.target.files[0], 'media-block')
        this.content.media.mobile = media.data.url
        this.uploading.mobile = false
      } catch (error) {
        this.uploading.mobile = false
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong.',
          variant: 'danger',
        })
      }
    },
  },
}
</script>

<style scoped>
.no-dimensions {
  width: 0;
  height: 0;
}

.cursor-pointer {
  cursor: pointer;
}
</style>